import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { QrCode } from 'lucide-react';
import { ApiError } from '@/api/client';
import { trackersApi, type Tracker, type TrackerModel } from '@/api/trackers';
import { TrackerLabelDialog } from '@/components/trackers/TrackerLabelDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { INLINE_ERROR_META } from '@/lib/mutationMeta';
import { labelAfterSave } from '@/lib/trackerLabel';
import {
  TRACKERS_PAGE_SIZE,
  formatPurchaseDate,
  iccidInput,
  imeiInput,
  listQuery,
  manufacturersOf,
  pageCount,
  rangeLabel,
  readListState,
  serverErrorMessage,
  shouldRereadUrl,
  todayIso,
  validateTrackerForm,
  writeListState,
  type StatusFilter,
  type TrackerFormField,
  type TrackerFormValues,
  type TrackerListState,
} from '@/lib/trackers';

/// Tela `/trackers` — inventário dos rastreadores dedicados (#1172). Gate:
/// `verified_organizer`, no AuthGuard da rota e na policy do backend.
///
/// Filtros, busca e página vivem na URL: recarregar ou mandar o link mantém a
/// mesma lista. Exclusão é lógica (status "inativo"), porque o vínculo IMEI ↔
/// inscrição vai apontar pra estes registros.
export function TrackersPage() {
  const qc = useQueryClient();
  // A URL é ESPELHO, não a fonte: lida uma vez na abertura e reescrita a cada
  // mudança. No React Router 7 a navegação roda em `startTransition`, então
  // derivar o filtro de `useSearchParams` fazia a 2ª interação seguida partir
  // de uma URL ainda velha e desfazer a 1ª (trocar fabricante logo depois de
  // "Limpar filtros" ressuscitava a busca antiga). O `useState` com updater
  // compõe as mudanças na ordem em que acontecem.
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<TrackerListState>(() => readListState(params));
  // O espelho reage só ao `state`. Com o `setParams` nas dependências, ele
  // rodava a cada mudança de URL (o `setParams` muda de identidade junto) e
  // reescrevia o filtro antigo por cima de uma navegação de fora.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  useEffect(() => {
    setParamsRef.current(writeListState(state), { replace: true });
  }, [state]);

  // Navegação de FORA relê a URL: o link do menu ("Rastreadores" com filtro
  // ativo) e o voltar/avançar do navegador. Quem decide é `shouldRereadUrl`.
  const location = useLocation();
  const navigationType = useNavigationType();
  const mountKey = useRef(location.key);
  useEffect(() => {
    if (!shouldRereadUrl(location.key, mountKey.current, navigationType)) return;
    setState(readListState(new URLSearchParams(location.search)));
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps -- só a navegação dispara

  /// Troca de filtro volta pra página 1; trocar só a página, não.
  function update(patch: Partial<TrackerListState>) {
    setState((prev) => ({
      ...prev,
      ...patch,
      page: 'page' in patch ? (patch.page ?? 1) : 1,
    }));
  }

  const [searchInput, setSearchInput] = useState(state.q);
  useEffect(() => setSearchInput(state.q), [state.q]);

  const models = useQuery({
    queryKey: ['tracker-models'],
    queryFn: trackersApi.models,
    staleTime: 5 * 60_000,
  });
  const manufacturers = useMemo(() => manufacturersOf(models.data ?? []), [models.data]);
  const modelOptions = useMemo(
    () => (models.data ?? []).filter((m) => !state.manufacturer || m.manufacturer === state.manufacturer),
    [models.data, state.manufacturer],
  );

  const query = listQuery(state);
  const page = useQuery({
    queryKey: ['trackers', query],
    queryFn: () => trackersApi.list(query),
    placeholderData: keepPreviousData,
  });

  const rows = page.data?.items ?? [];
  const total = page.data?.total ?? 0;
  const pages = pageCount(total, TRACKERS_PAGE_SIZE);

  // Página além do fim (ex.: inativou o último item da última página com o
  // filtro "ativos"): volta pra última que existe em vez de mostrar vazio.
  useEffect(() => {
    if (page.isSuccess && !page.isPlaceholderData && rows.length === 0 && total > 0 && state.page > pages) {
      update({ page: pages });
    }
  }, [page.isSuccess, page.isPlaceholderData, rows.length, total, state.page, pages]);

  const [editing, setEditing] = useState<Tracker | 'new' | null>(null);
  const [labelFor, setLabelFor] = useState<{ tracker: Tracker; imeiChanged: boolean } | null>(null);
  const [confirmInactivate, setConfirmInactivate] = useState<Tracker | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      trackersApi.setStatus(id, status),
    onSuccess: () => {
      setStatusError(null);
      qc.invalidateQueries({ queryKey: ['trackers'] });
    },
    // O erro aparece na própria tela (abaixo), então sem o toast global.
    meta: INLINE_ERROR_META,
    onError: (_e, { status }) =>
      setStatusError(
        status === 'inactive'
          ? 'Não foi possível inativar o rastreador. Tente novamente.'
          : 'Não foi possível reativar o rastreador. Tente novamente.',
      ),
  });

  const hasFilter = !!(state.manufacturer || state.modelId || state.q || state.status !== 'active');

  return (
    <div>
      <header className="border-b">
        <div className="container mx-auto py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-lg sm:text-xl font-semibold">Rastreadores</h1>
          <Button size="sm" onClick={() => setEditing('new')} disabled={!models.data?.length}>
            Novo rastreador
          </Button>
        </div>
      </header>

      <main className="container mx-auto py-6 sm:py-10 space-y-6 max-w-6xl">
        {/* ─── Filtros ─────────────────────────────────────────────── */}
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="filter-manufacturer">Fabricante</Label>
                <Select
                  id="filter-manufacturer"
                  value={state.manufacturer}
                  // Modelo de outro fabricante não sobrevive à troca.
                  onChange={(e) => update({ manufacturer: e.target.value, modelId: '' })}
                >
                  <option value="">Todos</option>
                  {manufacturers.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-model">Modelo</Label>
                <Select
                  id="filter-model"
                  value={state.modelId}
                  onChange={(e) => update({ modelId: e.target.value })}
                >
                  <option value="">Todos</option>
                  {modelOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {state.manufacturer ? m.model : `${m.manufacturer} · ${m.model}`}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="filter-status">Status</Label>
                <Select
                  id="filter-status"
                  value={state.status}
                  onChange={(e) => update({ status: e.target.value as StatusFilter })}
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                  <option value="">Todos</option>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="filter-q">IMEI, ICCID ou nº de série</Label>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    update({ q: searchInput.trim() });
                  }}
                  className="flex gap-2"
                >
                  <Input
                    id="filter-q"
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Trecho do IMEI, do ICCID ou do número de série"
                    className="flex-1 min-w-0"
                    autoComplete="off"
                  />
                  <Button type="submit" variant="outline" className="shrink-0">
                    Buscar
                  </Button>
                </form>
              </div>
            </div>
            {hasFilter && (
              <button
                type="button"
                onClick={() => setState(readListState(new URLSearchParams()))}
                className="text-xs text-muted-foreground hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </CardContent>
        </Card>

        {statusError && (
          <p className="text-sm text-destructive" role="alert">
            {statusError}
          </p>
        )}

        {/* ─── Tabela ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Inventário</CardTitle>
            {page.isSuccess && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {rangeLabel(state.page, TRACKERS_PAGE_SIZE, rows.length, total)}
              </span>
            )}
          </CardHeader>
          <CardContent>
            {page.isLoading && (
              <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Carregando rastreadores…
              </div>
            )}

            {page.isError && (
              <div className="py-8 text-center">
                <p className="text-sm text-destructive">
                  Erro ao carregar rastreadores. {page.error instanceof Error ? page.error.message : ''}
                </p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => page.refetch()}>
                  Tentar novamente
                </Button>
              </div>
            )}

            {page.isSuccess && rows.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {hasFilter ? 'Nenhum rastreador com esses filtros.' : 'Nenhum rastreador cadastrado ainda.'}
              </div>
            )}

            {page.isSuccess && rows.length > 0 && (
              <div className={page.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
                <TrackersTable
                  rows={rows}
                  busyId={setStatus.isPending ? setStatus.variables?.id : undefined}
                  onEdit={setEditing}
                  onLabel={(t) => setLabelFor({ tracker: t, imeiChanged: false })}
                  onInactivate={setConfirmInactivate}
                  onReactivate={(t) => setStatus.mutate({ id: t.id, status: 'active' })}
                />
              </div>
            )}

            {page.isSuccess && total > TRACKERS_PAGE_SIZE && (
              <div className="flex items-center justify-end gap-2 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={state.page <= 1 || page.isFetching}
                  onClick={() => update({ page: state.page - 1 })}
                >
                  Anterior
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums px-1">
                  Página {Math.min(state.page, pages)} de {pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={state.page >= pages || page.isFetching}
                  onClick={() => update({ page: state.page + 1 })}
                >
                  Próxima
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {editing != null && models.data && (
        <TrackerFormDialog
          tracker={editing === 'new' ? null : editing}
          models={models.data}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            const label = labelAfterSave(editing === 'new' ? null : editing, saved);
            if (label) setLabelFor({ tracker: saved, ...label });
          }}
        />
      )}

      {labelFor && (
        <TrackerLabelDialog
          tracker={labelFor.tracker}
          imeiChanged={labelFor.imeiChanged}
          onClose={() => setLabelFor(null)}
        />
      )}

      <ConfirmDialog
        open={confirmInactivate != null}
        onOpenChange={(o) => !o && setConfirmInactivate(null)}
        title="Inativar rastreador?"
        description={
          confirmInactivate
            ? `O ${confirmInactivate.manufacturer} ${confirmInactivate.model} de IMEI ${confirmInactivate.imei} sai da lista de ativos. O registro fica guardado e pode ser reativado pelo filtro "Inativos".`
            : undefined
        }
        confirmLabel="Inativar"
        destructive
        onConfirm={() =>
          confirmInactivate && setStatus.mutate({ id: confirmInactivate.id, status: 'inactive' })
        }
      />
    </div>
  );
}

function TrackersTable({
  rows,
  busyId,
  onEdit,
  onLabel,
  onInactivate,
  onReactivate,
}: {
  rows: Tracker[];
  busyId: string | undefined;
  onEdit: (t: Tracker) => void;
  onLabel: (t: Tracker) => void;
  onInactivate: (t: Tracker) => void;
  onReactivate: (t: Tracker) => void;
}) {
  return (
    <>
      {/* Celular: cartões empilhados com as ações à vista. A tela é usada na
          entrega dos aparelhos na praia, e uma tabela de 7 colunas rolando na
          horizontal escondia "Editar"/"Inativar" fora da tela. */}
      <ul className="md:hidden divide-y -mx-2">
        {rows.map((t) => {
          const inactive = t.status === 'inactive';
          return (
            <li key={t.id} className={`px-2 py-3 space-y-2 ${inactive ? 'text-muted-foreground' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">
                  {t.manufacturer} · {t.model}
                </span>
                <StatusChip inactive={inactive} />
              </div>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">IMEI</dt>
                <dd className="font-mono text-xs tabular-nums self-center break-all">{t.imei}</dd>
                <dt className="text-muted-foreground">Nº de série</dt>
                <dd className="font-mono text-xs self-center break-all">{t.serialNumber}</dd>
                <dt className="text-muted-foreground">ICCID</dt>
                <dd
                  className={`font-mono text-xs tabular-nums self-center break-all ${t.iccid ? '' : 'text-muted-foreground'}`}
                >
                  {t.iccid ?? '—'}
                </dd>
                <dt className="text-muted-foreground">Compra</dt>
                <dd className="tabular-nums">{formatPurchaseDate(t.purchaseDate)}</dd>
              </dl>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" className="min-h-[40px] gap-1" onClick={() => onLabel(t)}>
                  <QrCode className="h-4 w-4" /> Etiqueta
                </Button>
                <Button variant="outline" size="sm" className="min-h-[40px]" onClick={() => onEdit(t)}>
                  Editar
                </Button>
                {inactive ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[40px]"
                    disabled={busyId === t.id}
                    onClick={() => onReactivate(t)}
                  >
                    Reativar
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[40px] text-destructive hover:text-destructive"
                    disabled={busyId === t.id}
                    onClick={() => onInactivate(t)}
                  >
                    Inativar
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 px-2 font-medium">Fabricante</th>
              <th className="py-2 px-2 font-medium">Modelo</th>
              <th className="py-2 px-2 font-medium">IMEI</th>
              <th className="py-2 px-2 font-medium">Nº de série</th>
              <th className="py-2 px-2 font-medium">ICCID</th>
              {/* "Compra", como no cartão do celular: com a coluna ICCID, o
                  rótulo longo empurrava "Inativar" pra rolagem em 1280 px. */}
              <th className="py-2 px-2 font-medium">Compra</th>
              <th className="py-2 px-2 font-medium">Status</th>
              <th className="py-2 px-2 font-medium text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const inactive = t.status === 'inactive';
              return (
                <tr
                  key={t.id}
                  className={`border-b last:border-0 ${inactive ? 'text-muted-foreground' : ''}`}
                >
                  <td className="py-2 px-2 whitespace-nowrap">{t.manufacturer}</td>
                  <td className="py-2 px-2 whitespace-nowrap">{t.model}</td>
                  <td className="py-2 px-2 whitespace-nowrap font-mono text-xs tabular-nums">{t.imei}</td>
                  <td className="py-2 px-2 whitespace-nowrap font-mono text-xs">{t.serialNumber}</td>
                  <td
                    className={`py-2 px-2 whitespace-nowrap font-mono text-xs tabular-nums ${t.iccid ? '' : 'text-muted-foreground'}`}
                  >
                    {t.iccid ?? '—'}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap tabular-nums">
                    {formatPurchaseDate(t.purchaseDate)}
                  </td>
                  <td className="py-2 px-2">
                    <StatusChip inactive={inactive} />
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="gap-1" onClick={() => onLabel(t)}>
                      <QrCode className="h-4 w-4" /> Etiqueta
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onEdit(t)}>
                      Editar
                    </Button>
                    {inactive ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyId === t.id}
                        onClick={() => onReactivate(t)}
                      >
                        Reativar
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={busyId === t.id}
                        onClick={() => onInactivate(t)}
                      >
                        Inativar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StatusChip({ inactive }: { inactive: boolean }) {
  return (
    <span
      className={
        inactive
          ? 'text-xs rounded-full px-2 py-0.5 bg-zinc-500/15 text-zinc-700 dark:text-zinc-300 border border-zinc-500/30'
          : 'text-xs rounded-full px-2 py-0.5 bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 border border-emerald-500/40'
      }
    >
      {inactive ? 'inativo' : 'ativo'}
    </span>
  );
}

/// Cadastro e edição no mesmo formulário. Não reusa o `ConfirmDialog` porque
/// ele fecha ao confirmar, e aqui o 409 de IMEI duplicado precisa manter o
/// formulário aberto com o campo marcado. O shell (overlay, posição, safe-area)
/// é o mesmo dele.
function TrackerFormDialog({
  tracker,
  models,
  onClose,
  onSaved,
}: {
  tracker: Tracker | null;
  models: TrackerModel[];
  onClose: () => void;
  onSaved: (saved: Tracker) => void;
}) {
  const qc = useQueryClient();
  const [values, setValues] = useState<TrackerFormValues>(() => ({
    trackerModelId: tracker?.trackerModelId ?? (models.length === 1 ? models[0].id : ''),
    imei: tracker?.imei ?? '',
    serialNumber: tracker?.serialNumber ?? '',
    iccid: tracker?.iccid ?? '',
    purchaseDate: tracker?.purchaseDate ?? '',
  }));
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<{
    field: TrackerFormField | null;
    message: string;
  } | null>(null);

  const today = todayIso();
  const errors = validateTrackerForm(values, today);
  const shown = touched ? errors : {};
  const fieldError = (f: TrackerFormField) =>
    shown[f] ?? (serverError?.field === f ? serverError.message : undefined);

  const save = useMutation({
    mutationFn: (v: TrackerFormValues) => {
      const body = { ...v, imei: v.imei.trim(), iccid: v.iccid.trim() };
      return tracker ? trackersApi.update(tracker.id, body) : trackersApi.create(body);
    },
    // O erro vai pro campo (IMEI/serial duplicado) ou pro rodapé do formulário;
    // o toast genérico "Erro 409 / duplicate_imei" só repetiria em código cru.
    meta: INLINE_ERROR_META,
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['trackers'] });
      onClose();
      onSaved(saved);
    },
    onError: (e) => {
      const body =
        e instanceof ApiError && e.body && typeof e.body === 'object'
          ? (e.body as { error?: string; heldByImei?: string | null; heldByStatus?: string | null })
          : undefined;
      setServerError(serverErrorMessage(body?.error, body?.heldByImei, body?.heldByStatus));
    },
  });

  function set<K extends TrackerFormField>(k: K, v: string) {
    setValues((prev) => ({ ...prev, [k]: v }));
    if (serverError?.field === k || serverError?.field === null) setServerError(null);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    save.mutate(values);
  }

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className="
            fixed z-50 bg-background shadow-xl
            inset-x-0 top-0 rounded-b-xl pt-[env(safe-area-inset-top)]
            md:inset-auto md:left-1/2 md:top-[12%] md:-translate-x-1/2
            md:w-full md:max-w-md md:rounded-lg md:pt-0
            data-[state=open]:animate-in data-[state=open]:slide-in-from-top-4
          "
        >
          <form onSubmit={submit} noValidate className="space-y-4 p-4">
            <Dialog.Title className="text-base font-semibold">
              {tracker ? 'Editar rastreador' : 'Novo rastreador'}
            </Dialog.Title>

            <Field id="tracker-model" label="Fabricante e modelo" error={fieldError('trackerModelId')}>
              <Select
                id="tracker-model"
                value={values.trackerModelId}
                onChange={(e) => set('trackerModelId', e.target.value)}
              >
                <option value="">Escolha…</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.manufacturer} · {m.model}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              id="tracker-imei"
              label="IMEI"
              error={fieldError('imei')}
              // O IMEI se corrige aqui enquanto o aparelho não tem histórico (a
              // trava depois do vínculo é da #1172). É o que resolve o IMEI da
              // caixa errada: corrigindo este, o aparelho certo consegue entrar.
              hint={
                tracker
                  ? 'Digitou errado ou trocou as caixas? Corrija aqui.'
                  : '15 dígitos, na etiqueta do aparelho.'
              }
            >
              <Input
                id="tracker-imei"
                inputMode="numeric"
                autoComplete="off"
                value={values.imei}
                onChange={(e) => set('imei', imeiInput(e.target.value))}
                className="font-mono"
              />
            </Field>

            <Field id="tracker-serial" label="Número de série" error={fieldError('serialNumber')}>
              <Input
                id="tracker-serial"
                autoComplete="off"
                value={values.serialNumber}
                onChange={(e) => set('serialNumber', e.target.value)}
                className="font-mono"
              />
            </Field>

            <Field
              id="tracker-iccid"
              label="ICCID do chip (opcional)"
              error={fieldError('iccid')}
              // Opcional: o aparelho entra antes de o chip chegar. O chip troca
              // de aparelho; pra mover, apaga-se o ICCID do antigo (o 409 diz qual).
              hint={
                tracker
                  ? 'Trocou o chip? Atualize aqui. Deixe em branco se o aparelho está sem chip.'
                  : '19 ou 20 dígitos impressos no chip (os da Vivo começam com 8955). Pode ficar em branco até o chip chegar.'
              }
            >
              <Input
                id="tracker-iccid"
                inputMode="numeric"
                autoComplete="off"
                value={values.iccid}
                onChange={(e) => set('iccid', iccidInput(e.target.value))}
                className="font-mono"
              />
            </Field>

            <Field id="tracker-purchase" label="Data da compra" error={fieldError('purchaseDate')}>
              <Input
                id="tracker-purchase"
                type="date"
                max={today}
                value={values.purchaseDate}
                onChange={(e) => set('purchaseDate', e.target.value)}
              />
            </Field>

            {serverError?.field === null && (
              <p className="text-sm text-destructive" role="alert">
                {serverError.message}
              </p>
            )}

            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
                Cancelar
              </Button>
              <Button type="submit" disabled={save.isPending} className="w-full sm:w-auto">
                {save.isPending ? (
                  <>
                    <Spinner className="h-3.5 w-3.5" /> Salvando…
                  </>
                ) : tracker ? (
                  'Salvar'
                ) : (
                  'Cadastrar'
                )}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
